import EmptyState from '@/components/EmptyState';
import {
  useChangeWorkerRole,
  useInviteWorker,
  useRevokeWorker,
  useSetWorkerActive,
  useStaff,
} from '@/features/staff/hooks/useStaff';
import { ApiError } from '@/api/errors';
import type { StaffMember, WorkerRole } from '@restaurant/api-client';
import {
  Badge,
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useState } from 'react';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';

const ROLE_LABEL: Record<WorkerRole, string> = {
  MANAGER: 'Manager',
  STAFF: 'Server',
  KITCHEN: 'Kitchen',
  CASHIER: 'Cashier',
};
const ROLES: WorkerRole[] = ['MANAGER', 'STAFF', 'KITCHEN', 'CASHIER'];

function toast(text: string) {
  f7.toast.show({ text, closeTimeout: 2500, position: 'bottom' });
}

function StaffPage() {
  const settingsBackLink = useSettingsDetailBackLink();
  const { data: staff, isLoading, error } = useStaff();
  const invite = useInviteWorker();
  const changeRole = useChangeWorkerRole();
  const setActive = useSetWorkerActive();
  const revoke = useRevokeWorker();

  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<WorkerRole>('STAFF');

  const doInvite = () => {
    if (!email.trim() || !name.trim()) {
      toast('Enter a name and email.');
      return;
    }
    invite.mutate(
      { email: email.trim(), name: name.trim(), role },
      {
        onSuccess: () => {
          toast('Invitation sent');
          setShowForm(false);
          setEmail('');
          setName('');
          setRole('STAFF');
        },
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not invite that person.'),
      },
    );
  };

  const onRoleChange = (m: StaffMember, next: WorkerRole) => {
    if (next === m.role) return;
    changeRole.mutate(
      { workerId: m.workerId, role: next },
      { onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not change role.') },
    );
  };

  const onToggleActive = (m: StaffMember) => {
    setActive.mutate(
      { workerId: m.workerId, isActive: !m.isActive },
      { onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not update.') },
    );
  };

  const confirmRevoke = (m: StaffMember) => {
    f7.dialog.confirm(`Remove ${m.name} from this restaurant?`, 'Remove staff', () => {
      revoke.mutate(m.workerId, {
        onSuccess: () => toast(`${m.name} removed`),
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not remove.'),
      });
    });
  };

  const list = staff ?? [];

  return (
    <Page name="staff">
      <Navbar title="Staff" backLink={settingsBackLink}>
        <NavRight>
          <F7Link iconF7="plus" onClick={() => setShowForm((v) => !v)} />
        </NavRight>
      </Navbar>

      {showForm && (
        <>
          <BlockTitle>Invite a team member</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label="Name"
              type="text"
              placeholder="Jane Smith"
              value={name}
              onInput={(e) => setName(e.target.value)}
            />
            <ListInput
              label="Email"
              type="email"
              placeholder="jane@example.com"
              value={email}
              onInput={(e) => setEmail(e.target.value)}
            />
            <ListInput
              label="Role"
              type="select"
              value={role}
              onInput={(e) => setRole(e.target.value as WorkerRole)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </ListInput>
          </List>
          <Block>
            <Button fill large onClick={doInvite} disabled={invite.isPending}>
              {invite.isPending ? 'Inviting…' : 'Send invite'}
            </Button>
          </Block>
        </>
      )}

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}
      {error && (
        <EmptyState
          icon="exclamationmark_triangle"
          iconColor="#ff9500"
          title="Couldn’t load staff"
          description={(error as Error).message}
        />
      )}

      {staff && list.length === 0 && !showForm && (
        <EmptyState
          icon="person_2"
          title="No staff yet"
          description="Invite your team - waiters, kitchen, cashiers - and set what each can do. Tap +."
        />
      )}

      {list.length > 0 && (
        <List strongIos strongMd outlineIos outlineMd dividersIos dividersMd mediaList>
          {list.map((m) => (
            <ListItem key={m.workerId} swipeout title={m.name} subtitle={m.email ?? undefined}>
              <div slot="after-title">{!m.isActive && <Badge color="gray">Inactive</Badge>}</div>
              <div
                slot="content-end"
                className="display-flex align-items-center"
                style={{ gap: 8 }}
              >
                <select
                  value={m.role}
                  disabled={!m.isActive}
                  onChange={(e) => onRoleChange(m, e.target.value as WorkerRole)}
                  aria-label={`Role for ${m.name}`}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
                <ToggleSwitch checked={m.isActive} onChange={() => onToggleActive(m)} />
              </div>
              <div slot="swipeout-actions-right">
                <button
                  type="button"
                  className="swipeout-close color-red"
                  onClick={() => confirmRevoke(m)}
                >
                  Remove
                </button>
              </div>
            </ListItem>
          ))}
        </List>
      )}
    </Page>
  );
}

export default StaffPage;
